/**
 * Buscador de artículos de la base de conocimiento dentro del chat.
 * Se activa con un botón o atajo, busca artículos y permite enviar la respuesta rápida al chat.
 *
 * Layout: popup absolutamente posicionado encima del composer (`bottom: 100%`).
 * El wrapper externo conserva CSS estructural (position absolute + flex column +
 * shadow/borderRadius custom de popup) — Polaris Box no expone una primitiva
 * para popups absolutos. El contenido interno usa primitivas Polaris.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  BlockStack,
  Box,
  Button,
  Icon,
  InlineStack,
  Spinner,
  Text,
  TextField,
} from '@/lib/polaris';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  SearchIcon,
  XSmallIcon,
} from '@/lib/polaris-icons';
import { supabase } from '@/lib/supabase';

interface KbArticle {
  id: string;
  title: string;
  body: string;
  short_answer: string | null;
  category_name?: string;
}

interface Props {
  tenantId: string;
  onSelectArticle: (text: string, articleId: string) => void;
  onClose: () => void;
}

export function WaKbSearchPopover({ tenantId, onSelectArticle, onClose }: Props) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<KbArticle[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const search = useCallback(async (q: string) => {
    if (!q.trim()) { setResults([]); return; }
    setLoading(true);
    const { data } = await supabase
      .from('wa_kb_articles')
      .select('id, title, body, short_answer, category_id')
      .eq('tenant_id', tenantId)
      .eq('is_published', true)
      .or(`title.ilike.%${q}%,body.ilike.%${q}%,tags.cs.{${q}}`)
      .limit(10);

    if (data) {
      // Resolve category names
      const catIds = [...new Set((data as KbArticle[]).filter((a) => (a as unknown as Record<string, unknown>).category_id).map((a) => (a as unknown as Record<string, unknown>).category_id as string))];
      let catMap = new Map<string, string>();
      if (catIds.length > 0) {
        const { data: cats } = await supabase.from('wa_kb_categories').select('id, name').in('id', catIds);
        catMap = new Map((cats ?? []).map((c) => [c.id, c.name]));
      }
      setResults((data as (KbArticle & { category_id?: string })[]).map((a) => ({
        ...a,
        category_name: a.category_id ? catMap.get(a.category_id) : undefined,
      })));
    }
    setLoading(false);
  }, [tenantId]);

  useEffect(() => {
    const timer = setTimeout(() => search(query), 300);
    return () => clearTimeout(timer);
  }, [query, search]);

  // Load recent on mount
  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data } = await supabase
        .from('wa_kb_articles')
        .select('id, title, body, short_answer')
        .eq('tenant_id', tenantId)
        .eq('is_published', true)
        .order('used_count', { ascending: false })
        .limit(5);
      if (data) setResults(data as KbArticle[]);
      setLoading(false);
    })();
  }, [tenantId]);

  const handleSelect = async (art: KbArticle) => {
    const text = art.short_answer || art.body;
    onSelectArticle(text, art.id);
    // Increment used_count
    await supabase.rpc('increment_field', { table_name: 'wa_kb_articles', row_id: art.id, field_name: 'used_count' }).then(() => {}, () => {
      // Fallback
      supabase.from('wa_kb_articles').update({ used_count: ((art as unknown as Record<string, unknown>).used_count as number + 1) || 1 }).eq('id', art.id);
    });
  };

  return (
    <div
      style={{
        position: 'absolute', bottom: '100%', left: 0, right: 0,
        maxHeight: 360, background: 'var(--p-color-bg-surface)',
        border: '1px solid var(--p-color-border-subdued)',
        borderRadius: '12px 12px 0 0',
        boxShadow: '0 -4px 16px rgba(0,0,0,0.1)',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden', zIndex: 50,
      }}
    >
      {/* Header */}
      <Box paddingInlineStart="400" paddingInlineEnd="200" paddingBlockStart="250" paddingBlockEnd="150">
        <InlineStack align="space-between" blockAlign="center" wrap={false}>
          <Text as="span" variant="headingSm">Base de conocimiento</Text>
          <Button
            icon={XSmallIcon}
            variant="tertiary"
            onClick={onClose}
            accessibilityLabel="Cerrar"
          />
        </InlineStack>
      </Box>

      {/* Search */}
      <Box paddingInline="400" paddingBlockEnd="200">
        <TextField
          label=""
          labelHidden
          value={query}
          onChange={setQuery}
          placeholder="Buscar artículo…"
          prefix={<Icon source={SearchIcon} />}
          autoComplete="off"
          autoFocus
          clearButton
          onClearButtonClick={() => setQuery('')}
          size="slim"
        />
      </Box>

      {/* Results — el wrapper conserva `flex: 1 + overflowY: auto` en CSS porque
          Polaris Box no expone `flexGrow` como prop tipada. */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {loading ? (
          <Box padding="500">
            <InlineStack align="center"><Spinner size="small" /></InlineStack>
          </Box>
        ) : results.length === 0 ? (
          <Box padding="500">
            <Text as="p" variant="bodySm" tone="subdued" alignment="center">
              {query ? 'Sin resultados' : 'Escribe para buscar…'}
            </Text>
          </Box>
        ) : (
          results.map((art) => {
            const expanded = expandedId === art.id;
            return (
              <Box key={art.id} borderBlockEndWidth="025" borderColor="border-subdued">
                <button
                  type="button"
                  onClick={() => setExpandedId(expanded ? null : art.id)}
                  className="wa-kb-row"
                  aria-expanded={expanded}
                >
                  <Box paddingInline="400" paddingBlock="250">
                    <InlineStack align="space-between" blockAlign="center" wrap={false} gap="200">
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <BlockStack gap="050">
                          <Text as="p" variant="bodySm" fontWeight="semibold" truncate>{art.title}</Text>
                          {art.category_name ? (
                            <Text as="p" variant="bodySm" tone="subdued">{art.category_name}</Text>
                          ) : null}
                        </BlockStack>
                      </div>
                      <Icon
                        source={expanded ? ChevronDownIcon : ChevronRightIcon}
                        tone="subdued"
                      />
                    </InlineStack>
                  </Box>
                </button>
                {expanded ? (
                  <Box paddingInline="400" paddingBlockEnd="250">
                    <BlockStack gap="200">
                      <Box background="bg-surface-secondary" padding="250" borderRadius="200">
                        {/* maxHeight scroll requiere style — Polaris Box.maxHeight no acepta px arbitrarios. */}
                        <div style={{ maxHeight: 120, overflowY: 'auto' }}>
                          <Text as="p" variant="bodySm">{art.short_answer || art.body.slice(0, 300)}</Text>
                        </div>
                      </Box>
                      <Button
                        variant="primary"
                        tone="success"
                        fullWidth
                        onClick={() => void handleSelect(art)}
                      >
                        Usar en chat
                      </Button>
                    </BlockStack>
                  </Box>
                ) : null}
              </Box>
            );
          })
        )}
      </div>
    </div>
  );
}
